import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import type { LeadStatus } from '@conversia/shared-types';
import * as conversationsApi from '@/api/conversations.api';
import * as channelsApi from '@/api/channels.api';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConversationItem } from './ConversationItem';
import { HelpTip } from '@/components/ui/HelpTip';
import { useConversationsStore } from '@/store/conversations.store';
import { channelTypeLabel } from '@/lib/badges';
import { cn } from '@/lib/cn';

// Pestañas por TEMPERATURA del lead (embudo) + "Atención" (alta A1 de la
// auditoría): las conversaciones en WAITING_HUMAN — a quién le debo respuesta
// YA — con filtro propio en vez de buscar bordes ámbar entre cien filas.
const ATTN_TAB = '__ATTN' as const;
const LEAD_TABS: { value: LeadStatus | '' | typeof ATTN_TAB; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: ATTN_TAB, label: 'Atención' },
  { value: 'CALIENTE', label: 'Caliente' },
  { value: 'TIBIO', label: 'Tibio' },
  { value: 'FRIO', label: 'Frío' },
  { value: 'CERRADO', label: 'Cerrado' },
  { value: 'NO_CERRADO', label: 'No cerrado' },
];

const TYPES: { value: 'all' | 'individual' | 'group'; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'individual', label: 'Individuales' },
  { value: 'group', label: 'Grupos' },
];

/** Cuántos chats trae cada página. El servidor no da más de 200 por pedido. */
const POR_PAGINA = 100;

export function ConversationList({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  const filters = useConversationsStore((s) => s.filters);
  const setFilters = useConversationsStore((s) => s.setFilters);

  const { data: channels } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
  });

  // DE A PÁGINAS, Y NO "LOS 100 PRIMEROS".
  //
  // La lista pedía 100 y ahí se quedaba, sin avisar. Esteban tenía 214 chats el
  // 22-sep-2026: 114 no aparecían en ningún lado, y lo que él pidió era
  // textual *"que yo pueda ver todos los chats realmente"*. Ahora se cargan de
  // a 100, se dice cuántos hay en total y se ofrece cargar más.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['conversations', 'list', filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      conversationsApi.listConversations({
        status: filters.status,
        leadStatus: filters.leadStatus,
        channelId: filters.channelId,
        search: filters.search || undefined,
        type: filters.type && filters.type !== 'all' ? filters.type : undefined,
        skip: pageParam,
        take: POR_PAGINA,
      }),
    getNextPageParam: (ultima, todas) => {
      const cargados = todas.reduce((n, p) => n + p.items.length, 0);
      return cargados < ultima.total ? cargados : undefined;
    },
  });
  const total = data?.pages[0]?.total ?? 0;

  // Las conversaciones en "Atención" se muestran primero para que el
  // operador las atienda sin buscarlas. `sort` es estable, así que el resto
  // conserva el orden que devuelve la API (por última actividad).
  const items = useMemo(() => {
    const list = data?.pages.flatMap((p) => p.items) ?? [];
    return [...list].sort((a, b) => {
      const aw = a.status === 'WAITING_HUMAN' ? 0 : 1;
      const bw = b.status === 'WAITING_HUMAN' ? 0 : 1;
      return aw - bw;
    });
  }, [data]);

  return (
    <div className="flex h-full w-full flex-col border-r border-surface-border bg-white dark:border-slate-800 dark:bg-slate-900 sm:w-88">
      <div className="space-y-3 border-b border-surface-border p-4 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={filters.search ?? ''}
              onChange={(e) => setFilters({ search: e.target.value })}
              placeholder="Buscar contacto…"
              className="h-9 w-full rounded-md border border-surface-border bg-surface-muted pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
          <HelpTip helpKey="conversations" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_TABS.map((s) => {
            const isAttn = s.value === ATTN_TAB;
            const active = isAttn
              ? filters.status === 'WAITING_HUMAN'
              : !filters.status && (filters.leadStatus ?? '') === s.value;
            return (
              <button
                key={s.value || 'all'}
                onClick={() =>
                  s.value === ATTN_TAB
                    ? setFilters({ leadStatus: undefined, status: 'WAITING_HUMAN' })
                    : setFilters({ leadStatus: s.value || undefined, status: undefined })
                }
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  active
                    ? isAttn
                      ? 'bg-amber-500 text-white'
                      : 'bg-brand-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <div className="flex gap-1.5">
          {TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => setFilters({ type: t.value })}
              className={cn(
                'flex-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                (filters.type ?? 'all') === t.value
                  ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={filters.channelId ?? ''}
          onChange={(e) => setFilters({ channelId: e.target.value || undefined })}
          className="h-9 w-full rounded-md border border-surface-border bg-white px-2 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="">Todos los canales</option>
          {channels?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} · {channelTypeLabel[c.type]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Sin conversaciones" description="No hay conversaciones con estos filtros." className="m-4" />
        ) : (
          <ul className="divide-y divide-surface-border dark:divide-slate-800">
            {items.map((c) => (
              <li key={c.id}>
                <ConversationItem
                  conversation={c}
                  selected={selectedId === c.id}
                  onSelect={() => onSelect(c.id)}
                />
              </li>
            ))}
          </ul>
        )}
        {items.length > 0 && (
          <div className="space-y-2 p-3 text-center text-xs text-slate-500 dark:text-slate-400">
            <p>
              Mostrando {items.length} de {total}
            </p>
            {hasNextPage && (
              <button
                type="button"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                className="min-h-[40px] w-full rounded-lg border border-surface-border text-sm text-slate-700 hover:bg-surface-muted disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
              >
                {isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
